import { useState, useEffect, useRef } from "react";

interface SurveyData {
  wellbeingCare: number;
  resonance: number;
  amenityGap: number;
  gender: string;
  ageBracket: string;
  preferredExperience: string;
  concerns: string;
  ideas: string;
  wouldUse: string;
  ifValidated: string;
}

interface CBS1Props {
  onComplete: (data: SurveyData, qualified: boolean) => void;
  onDecline: () => void;
  onChatbotTrigger?: (visible: boolean) => void;
}

type SectionType = "survey" | "complete";

export default function CBS1({
  onComplete,
  onDecline,
  onChatbotTrigger,
}: CBS1Props) {
  const [surveyData, setSurveyData] = useState<SurveyData>({
    wellbeingCare: 5,
    resonance: 5,
    amenityGap: 5,
    gender: "",
    ageBracket: "",
    preferredExperience: "",
    concerns: "",
    ideas: "",
    wouldUse: "",
    ifValidated: "",
  });

  const [currentSection, setCurrentSection] = useState<SectionType>("survey");
  const [showValidation, setShowValidation] = useState(false);

  const chatbotTriggered = useRef(false);

  // Fire chatbot trigger when survey is submitted (once only)
  useEffect(() => {
    if (
      currentSection === "complete" &&
      onChatbotTrigger &&
      !chatbotTriggered.current
    ) {
      chatbotTriggered.current = true;
      setTimeout(() => {
        onChatbotTrigger(true);
      }, 800);
    }
  }, [currentSection, onChatbotTrigger]);

  const experiences = [
    {
      id: "solo",
      title: "Experience 01 — Solo",
      description:
        "Your private sanctuary. Controlled light, sound, temperature. Twenty minutes that belong entirely to you.",
    },
    {
      id: "mother-moment",
      title: "Experience 02 — Mother Moment",
      description:
        "Parent respite. Kids decompress alongside in a calm zone. Not separated - together but apart.",
    },
    {
      id: "vista",
      title: "Experience 03 — Vista",
      description:
        "Reset with a view. The world outside, stillness inside. Perspective restored.",
    },
    {
      id: "grove",
      title: "Experience 04 — The Grove",
      description:
        "Nature embedded in the city. Biophilic refuge. Living walls, natural textures. Urban forest bathing.",
    },
  ];

  const handleSliderChange = (field: keyof SurveyData, value: number) => {
    setSurveyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field: keyof SurveyData, value: string) => {
    setSurveyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTextChange = (field: keyof SurveyData, value: string) => {
    setSurveyData((prev) => ({ ...prev, [field]: value }));
  };

  const validateSurvey = () => {
    return (
      surveyData.gender !== "" &&
      surveyData.ageBracket !== "" &&
      surveyData.preferredExperience !== ""
    );
  };

  const handleSurveySubmit = () => {
    if (!validateSurvey()) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    // Store survey data
    onComplete(surveyData, true);
    // Show thank you, chatbot will overlay with gateway
    setCurrentSection("complete");
  };

  const renderSurvey = () => (
    <div className="space-y-12">
      <div className="text-center">
        <h2 className="font-pp-editorial text-3xl mb-3">
          We'd love to hear your thoughts
        </h2>
        <p className="text-cream/60 text-sm">
          Your responses help us build something that truly serves you
        </p>
      </div>

      <div className="space-y-10">
        <div className="space-y-4">
          <label className="block text-sm text-cream/80">
            How much do you care about your wellbeing day to day?
          </label>
          <div className="space-y-2">
            <input
              type="range"
              min="1"
              max="10"
              value={surveyData.wellbeingCare}
              onChange={(e) =>
                handleSliderChange("wellbeingCare", parseInt(e.target.value))
              }
              className="w-full h-1 bg-cream/20 rounded-lg appearance-none cursor-pointer slider-coral"
            />
            <div className="flex justify-between text-xs text-cream/40">
              <span>Not at all</span>
              <span className="text-coral font-medium">
                {surveyData.wellbeingCare}
              </span>
              <span>Extremely</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-sm text-cream/80">
            Do you resonate with breathwork, meditation, and/or see the value in
            what we've highlighted to you?
          </label>
          <div className="space-y-2">
            <input
              type="range"
              min="1"
              max="10"
              value={surveyData.resonance}
              onChange={(e) =>
                handleSliderChange("resonance", parseInt(e.target.value))
              }
              className="w-full h-1 bg-cream/20 rounded-lg appearance-none cursor-pointer slider-coral"
            />
            <div className="flex justify-between text-xs text-cream/40">
              <span>Not at all</span>
              <span className="text-coral font-medium">
                {surveyData.resonance}
              </span>
              <span>Completely</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-sm text-cream/80">
            Do you think there's suitable amenity in cities to take a moment to
            safely retreat, rest, recover?
          </label>
          <div className="space-y-2">
            <input
              type="range"
              min="1"
              max="10"
              value={surveyData.amenityGap}
              onChange={(e) =>
                handleSliderChange("amenityGap", parseInt(e.target.value))
              }
              className="w-full h-1 bg-cream/20 rounded-lg appearance-none cursor-pointer slider-coral"
            />
            <div className="flex justify-between text-xs text-cream/40">
              <span>Definitely not</span>
              <span className="text-coral font-medium">
                {surveyData.amenityGap}
              </span>
              <span>Absolutely</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8 pt-4">
        <div className="space-y-4">
          <label className="block text-sm text-cream/80">Gender</label>
          <div className="grid grid-cols-2 gap-3">
            {["Male", "Female", "Non-binary", "Prefer not to say"].map(
              (option) => (
                <button
                  key={option}
                  onClick={() => handleSelectChange("gender", option)}
                  className={`py-3 px-4 rounded border transition-all ${
                    surveyData.gender === option
                      ? "border-coral bg-coral/10 text-coral"
                      : "border-cream/20 text-cream/60 hover:border-cream/40"
                  }`}
                >
                  {option}
                </button>
              )
            )}
          </div>
          {showValidation && surveyData.gender === "" && (
            <p className="text-xs text-coral">Please select your gender</p>
          )}
        </div>

        <div className="space-y-4">
          <label className="block text-sm text-cream/80">Age bracket</label>
          <div className="grid grid-cols-2 gap-3">
            {["18-24", "25-34", "35-44", "45-54", "55+"].map((option) => (
              <button
                key={option}
                onClick={() => handleSelectChange("ageBracket", option)}
                className={`py-3 px-4 rounded border transition-all ${
                  surveyData.ageBracket === option
                    ? "border-coral bg-coral/10 text-coral"
                    : "border-cream/20 text-cream/60 hover:border-cream/40"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          {showValidation && surveyData.ageBracket === "" && (
            <p className="text-xs text-coral">Please select your age bracket</p>
          )}
        </div>

        <div className="space-y-4">
          <label className="block text-sm text-cream/80">
            Four ways to reset - which experience would you use the most?
          </label>
          <div className="space-y-3">
            {experiences.map((exp) => (
              <button
                key={exp.id}
                onClick={() =>
                  handleSelectChange("preferredExperience", exp.id)
                }
                className={`w-full text-left p-4 rounded border transition-all ${
                  surveyData.preferredExperience === exp.id
                    ? "border-coral bg-coral/10"
                    : "border-cream/20 hover:border-cream/40"
                }`}
              >
                <div
                  className={`font-medium mb-1 ${
                    surveyData.preferredExperience === exp.id
                      ? "text-coral"
                      : "text-cream"
                  }`}
                >
                  {exp.title}
                </div>
                <div className="text-xs text-cream/60">{exp.description}</div>
              </button>
            ))}
          </div>
          {showValidation && surveyData.preferredExperience === "" && (
            <p className="text-xs text-coral">
              Please select your preferred experience
            </p>
          )}
        </div>

        <div className="space-y-4">
          <label className="block text-sm text-cream/80">
            Do you have any concerns about what you've seen, or a deal-breaker
            for why you wouldn't use it?
          </label>
          <textarea
            value={surveyData.concerns}
            onChange={(e) => handleTextChange("concerns", e.target.value)}
            placeholder="Optional - share your thoughts..."
            rows={4}
            className="w-full bg-charcoal-dark border border-cream/20 rounded px-4 py-3 text-cream placeholder-cream/40 focus:outline-none focus:border-coral transition-colors resize-none"
          />
        </div>

        <div className="space-y-4">
          <label className="block text-sm text-cream/80">
            Any ideas you think we missed?
          </label>
          <textarea
            value={surveyData.ideas}
            onChange={(e) => handleTextChange("ideas", e.target.value)}
            placeholder="Optional - we're all ears..."
            rows={4}
            className="w-full bg-charcoal-dark border border-cream/20 rounded px-4 py-3 text-cream placeholder-cream/40 focus:outline-none focus:border-coral transition-colors resize-none"
          />
        </div>
      </div>

      <div className="pt-6">
        <button
          onClick={handleSurveySubmit}
          className="w-full bg-coral text-charcoal-dark py-4 rounded font-medium hover:bg-coral/90 transition-colors"
        >
          Continue
        </button>

        <div className="text-center mt-6">
          <p className="text-xs text-cream/40 mb-4">
            All information is stored anonymously and will not be linked to your
            identity
          </p>
          <button
            onClick={onDecline}
            className="max-w-xs mx-auto w-full border border-cream/20 text-cream py-3 rounded font-medium hover:border-cream/40 transition-colors text-sm"
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="space-y-8 text-center">
      <div className="inline-block p-4 bg-coral/10 rounded-full mb-4">
        <svg
          className="w-12 h-12 text-coral"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <div>
        <h2 className="font-pp-editorial text-3xl mb-4">Thank you</h2>
        <p className="text-cream/60 text-sm mb-8">
          Your responses have been recorded
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-charcoal-dark text-cream p-6 flex items-center justify-center">
      <div className="w-full max-w-2xl">
        {currentSection === "survey" && renderSurvey()}
        {currentSection === "complete" && renderComplete()}
      </div>
    </div>
  );
}
